import { parse as PapaParse } from "papaparse";

type ParseCsvStringOutput = {
  /** List of annotations descriptions parsed from the CSV */
  annotations: AnnotationDescription[];

  /** Error message if parsing failed */
  errorMessage?: string;
};

/**
 * @param csvFile File object for the CSV picked by user
 * @returns the data read in the CSV
 */
export function getAnnotationsDescriptionFromCsvFile(
  csvFile: File,
): Promise<ParseCsvStringOutput> {
  return new Promise<ParseCsvStringOutput>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(parseCsvString(reader.result));
      } else {
        reject(new Error("Invalid data read"));
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.onabort = () => reject(new Error("Read aborted"));
    reader.readAsText(csvFile);
  });
}

/**
 * List of mandatory tags for headers in the CSV..
 */
export enum MandatoryHeadersValues {
  Name = "Name",
  GlobalPoseX = "GlobalPoseX",
  GlobalPoseY = "GlobalPoseY",
  GlobalPoseZ = "GlobalPoseZ",
}

/**
 * List of non mandatory tags for headers in the CSV..
 */
export enum NonMandatoryHeadersValues {
  ID = "ID",
  Assignee = "Assignee",
  CreatedAt = "Created At",
  CreatedBy = "Created By",
  DueDate = "Due Date",
  Status = "Status",
  Tags = "Tags",
  NumberOfAttachments = "Number of Attachments",
  Description = "Description",
  Url = "Url",
}

/**
 * List of supported tags for headers in the CSV..
 */
export type SupportedHeadersValues =
  | MandatoryHeadersValues
  | NonMandatoryHeadersValues;

/**
 * Type representing a single annotation description (record of expected headers to their values).
 * The name of each field MUST match the tag in the CSV. Use quotes for tags that would contain spaces.
 * Example: val1.ID vs val2["Created At"].
 */
export type AnnotationDescription = Record<SupportedHeadersValues, string>;

type GetHeadersIndexesOutput = {
  /** List of unexpected headers (if any) */
  unknownHeaders: string[];

  /** List of missing mandatory headers (if any) */
  missingHeaders: MandatoryHeadersValues[];
};

// Immutable array with the non mandatory headers values
const arrayNonMandatoryHeaders: readonly NonMandatoryHeadersValues[] =
  Object.values(NonMandatoryHeadersValues);

/**
 * @returns an array giving the ExpectedHeaders value matching each column in the CSV
 * @param headers list of headers extracted by papaparser
 */
export function getHeadersIndexes(headers: string[]): GetHeadersIndexesOutput {
  // Prepare the list of expected headers
  const arrayMandatoryHeaders = Object.values(MandatoryHeadersValues);

  const unknownHeaders: string[] = [];

  for (const headerMatch of headers) {
    const matchMandatory = arrayMandatoryHeaders.findIndex(
      (header) => header === headerMatch,
    );
    if (matchMandatory >= 0) {
      // remove this ExpectedHeaders value so we can get the list of missing headers
      arrayMandatoryHeaders.splice(matchMandatory, 1);
    } else {
      const matchNonMandatory = arrayNonMandatoryHeaders.findIndex(
        (header) => header === headerMatch,
      );
      if (matchNonMandatory < 0) {
        // add this header in the list of unknown ones
        unknownHeaders.push(headerMatch);
      }
    }
  }

  return {
    unknownHeaders,
    missingHeaders: arrayMandatoryHeaders,
  };
}

/** Output of parseCsvContents function */
type ParseCsvContentsOutput = {
  /** List of all headers read in the CSV, whether they are expected or not. */
  headers: string[];
  /** The CSV data read from the CSV. Valid only if no required header is missing. */
  data: AnnotationDescription[];
  /** Error message if parsing failed */
  errorMessage?: string;
};

/**
 * Parse the contents of a CSV file and return its data.
 *
 * @param csvContents contents of the CSV file to parse
 * @returns result of parsing as a ParseCsvContentsOutput
 */
function parseCsvContents(csvContents: string): ParseCsvContentsOutput {
  const csvData = PapaParse<AnnotationDescription>(csvContents, {
    // do not use automatic delimiter detection as papaparse could wrongly use spaces or tabs
    delimiter: ",",
    // ask papaparse to extract the list of headers and check the consistency of number of items in each line
    header: true,
    // ignore empty lines
    skipEmptyLines: true,
  });

  if (csvData.errors.length) {
    const errorMessages = csvData.errors.map(
      (error) => `${error.message} (line:${error.row}, index:${error.index})`,
    );
    return {
      headers: [],
      data: [],
      errorMessage: `Invalid CSV format (${errorMessages})`,
    };
  }

  return {
    headers: csvData.meta.fields ?? [],
    data: csvData.data,
  };
}

/**
 * Parse the contents of the annotations CSV form its text contents.
 *
 * @param csvContents contents of the CSV as a text
 * @returns An array of objects representing the parsed CSV data
 */
export function parseCsvString(csvContents: string): ParseCsvStringOutput {
  const csvData = parseCsvContents(csvContents);

  if (csvData.errorMessage) {
    return {
      annotations: [],
      errorMessage: csvData.errorMessage,
    };
  }

  if (csvData.data.length < 1) {
    return {
      annotations: [],
      errorMessage: "Invalid CSV format (expecting 2 lines including header)",
    };
  }

  // check actual headers vs expected headers
  if (csvData.headers.length === 0) {
    return {
      annotations: [],
      errorMessage: "No headers found in first line of the CSV.",
    };
  }

  // validate headers
  const headersDescription = getHeadersIndexes(csvData.headers);
  if (headersDescription.missingHeaders.length > 0) {
    return {
      annotations: [],
      errorMessage: `Missing headers in CSV: ${headersDescription.missingHeaders.join(", ")}`,
    };
  }
  if (headersDescription.unknownHeaders.length > 0) {
    return {
      annotations: [],
      errorMessage: `Unknown headers in CSV: ${headersDescription.unknownHeaders.join(", ")}`,
    };
  }

  // no issue found
  return { annotations: csvData.data };
}
